import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DotboxLogo from '@/components/common/DotboxLogo';
import { Eye, EyeOff, Loader2, Linkedin, Twitter, Facebook } from 'lucide-react';

const LoginPage = () => {
  // iter149 — prefill from `/login?email=x&invite=y` so the invite email
  // flow drops the user into a form already primed for their account.
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get('email') || '';
  const inviteToken = searchParams.get('invite');
  React.useEffect(() => {
    if (inviteToken) {
      try { localStorage.setItem('dotbox.pendingInviteToken', inviteToken); } catch { /* ignore */ }
    }
  }, [inviteToken]);
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [twitterLoading, setTwitterLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (requires2FA) {
      if (useRecoveryCode && recoveryCode.length !== 8) {
        toast.error('Please enter your 8-digit recovery code');
        return;
      }
      if (!useRecoveryCode && totpCode.length !== 6) {
        toast.error('Please enter your 6-digit 2FA code');
        return;
      }
    }

    setLoading(true);
    try {
      const result = await login(
        email,
        password,
        useRecoveryCode ? undefined : totpCode || undefined,
        useRecoveryCode ? recoveryCode || undefined : undefined,
      );
      if (result.requires_2fa) {
        setRequires2FA(true);
        setLoading(false);
        toast.info('Enter your two-factor authentication code');
        return;
      }
      // iter250 — account exists and the password was right, but the email
      // was never confirmed. Send them somewhere they can actually fix it.
      if (result.activation_required) {
        toast.info(result.error || 'Activate your account to continue');
        navigate(
          result.email
            ? `/activate?email=${encodeURIComponent(result.email)}`
            : '/activate',
        );
        return;
      }
      if (result.success) {
        // Short duration on the celebratory toast so it clears the header (and the
        // notification bell) fast — see iter107.6 UX polish note.
        toast.success('Welcome back!', { duration: 2000 });
        // iter149 — if the user came in from a team-invite email, bounce
        // straight back to the accept page so the invite auto-accepts.
        let pendingToken = null;
        try { pendingToken = localStorage.getItem('dotbox.pendingInviteToken'); } catch { /* localStorage may be disabled */ }
        if (pendingToken) {
          navigate(`/invite?token=${pendingToken}`);
        } else if (result.user?.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard/command-center');
        }
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecoveryMode = () => {
    setUseRecoveryCode((prev) => !prev);
    setTotpCode('');
    setRecoveryCode('');
  };

  const handleLinkedInLogin = async () => {
    try {
      setLinkedinLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/auth/linkedin`);
      const data = await res.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(data.detail || 'Failed to initiate LinkedIn login');
      }
    } catch (error) {
      toast.error('LinkedIn login unavailable');
    } finally {
      setLinkedinLoading(false);
    }
  };

  const handleTwitterLogin = async () => {
    try {
      setTwitterLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/auth/twitter`);
      const data = await res.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(data.detail || 'Failed to initiate X login');
      }
    } catch (error) {
      toast.error('X login unavailable');
    } finally {
      setTwitterLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    try {
      setFacebookLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/auth/facebook`);
      const data = await res.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(data.detail || 'Failed to initiate Facebook login');
      }
    } catch (error) {
      toast.error('Facebook login unavailable');
    } finally {
      setFacebookLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1024] constellation-bg relative flex items-center justify-center p-4 overflow-hidden">
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <DotboxLogo className="w-full justify-center mb-4 h-10" variant="dark" />
          <h1 className="text-2xl font-bold text-white">
            Welcome back
          </h1>
          <p className="text-slate-400 mt-2">
            Sign in to your account
          </p>
        </div>

        <Card className="shadow-xl shadow-black/40 border">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-center text-white">
              Sign In
            </CardTitle>
            <CardDescription className="text-center text-slate-400">
              Enter your credentials to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="h-11"
                  data-testid="login-email-input"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-red-400 hover:text-red-300 font-medium"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="h-11 pr-10"
                    data-testid="login-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-slate-300"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* 2FA Code Input - shown when required */}
              {requires2FA && !useRecoveryCode && (
                <div className="space-y-2" data-testid="login-2fa-totp-block">
                  <label htmlFor="totp" className="text-sm font-medium">Authentication Code</label>
                  <Input
                    id="totp"
                    type="text"
                    placeholder="6-digit code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    disabled={loading}
                    className="h-11 font-mono tracking-widest text-center text-lg"
                    data-testid="login-totp-input"
                    autoFocus
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">Enter the code from your authenticator app</p>
                    <button
                      type="button"
                      onClick={toggleRecoveryMode}
                      className="text-xs font-semibold text-red-400 hover:text-red-300 transition-colors"
                      data-testid="login-use-recovery-code-toggle"
                    >
                      Use a recovery code
                    </button>
                  </div>
                </div>
              )}

              {/* Recovery Code Input - shown when user opts in */}
              {requires2FA && useRecoveryCode && (
                <div className="space-y-2" data-testid="login-2fa-recovery-block">
                  <label htmlFor="recovery" className="text-sm font-medium">Recovery Code</label>
                  <Input
                    id="recovery"
                    type="text"
                    placeholder="8-digit recovery code"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    maxLength={8}
                    disabled={loading}
                    className="h-11 font-mono tracking-widest text-center text-lg"
                    data-testid="login-recovery-code-input"
                    autoFocus
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">Enter one of your single-use backup codes</p>
                    <button
                      type="button"
                      onClick={toggleRecoveryMode}
                      className="text-xs font-semibold text-red-400 hover:text-red-300 transition-colors"
                      data-testid="login-use-totp-toggle"
                    >
                      Use authenticator instead
                    </button>
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold shadow-lg shadow-red-900/30"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#1F2A4D]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-[#0F1730] text-slate-400">or continue with</span>
              </div>
            </div>

            {/* LinkedIn Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 font-semibold border-[#0A66C2]/60 bg-transparent text-white hover:bg-[#0A66C2]/15"
              onClick={handleLinkedInLogin}
              disabled={linkedinLoading || loading}
              data-testid="linkedin-login-button"
            >
              {linkedinLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</>
              ) : (
                <><Linkedin className="mr-2 h-5 w-5" /> Sign in with LinkedIn</>
              )}
            </Button>

            {/* X (Twitter) Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 mt-3 font-semibold border-[#2A3658] bg-transparent text-slate-200 hover:bg-[#131C3A] hover:text-white"
              onClick={handleTwitterLogin}
              disabled={twitterLoading || loading}
              data-testid="twitter-login-button"
            >
              {twitterLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</>
              ) : (
                <><Twitter className="mr-2 h-5 w-5" /> Sign in with X</>
              )}
            </Button>

            {/* Facebook Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 mt-3 font-semibold border-[#1877F2]/60 bg-transparent text-white hover:bg-[#1877F2]/15"
              onClick={handleFacebookLogin}
              disabled={facebookLoading || loading}
              data-testid="facebook-login-button"
            >
              {facebookLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</>
              ) : (
                <><Facebook className="mr-2 h-5 w-5" /> Sign in with Facebook</>
              )}
            </Button>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-400">
                Don&apos;t have an account?{' '}
                <Link 
                  to="/register" 
                  className="font-semibold text-red-400 hover:text-red-300 transition-colors"
                  data-testid="login-register-link"
                >
                  Sign up for free
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            ← Back to homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
